/*
  Display-currency converter.

  WHAT THIS IS, AND WHAT IT IS NOT. Prices are SHOWN in the visitor's currency;
  the customer is still CHARGED in AED at checkout. Presenting a market in its
  own currency needs Shopify Payments, which is early-access-only in the UAE
  (checked 25 Aug 2026). So this is a reading aid, and every surface that shows
  a converted figure says so — see the note the picker renders.

  Cart, drawer and checkout are deliberately untouched. Converting a cart total
  would put a number in front of the customer that they never actually pay.

  RATES. Five of the seven currencies here are PEGGED and have not moved in
  years, so they are exact rather than approximate:

      AED  3.6725 / USD   pegged 1997
      SAR  3.75   / USD   pegged
      BHD  0.376  / USD   pegged
      OMR  0.3845 / USD   pegged
      KWD  ~0.306 / USD   basket peg, drifts slightly
      EUR                 floats — the only one that really moves

  The baked table below is derived from those pegs and is the FALLBACK. A live
  refresh runs at most once a day and overrides it, which matters for EUR and
  KWD and changes nothing for the rest. If the fetch fails, the pegs stand and
  nothing breaks.
*/
(function () {
  'use strict';

  var SHOP = 'AED';
  var KEY = 'ef-currency';
  var RATES_KEY = 'ef-currency-rates';
  var DAY = 86400000;

  /* AED -> X. Pegged values, computed from the USD pegs above. */
  var FALLBACK = {
    AED: 1,
    USD: 0.272294,
    SAR: 1.021103,
    BHD: 0.102383,
    OMR: 0.104697,
    KWD: 0.083322,
    EUR: 0.252000,
  };

  /* Currencies this shop offers, in the order the client listed them. */
  var OFFERED = ['AED', 'SAR', 'BHD', 'OMR', 'KWD', 'EUR', 'USD'];

  var LABELS = {
    AED: 'UAE', SAR: 'Saudi Arabia', BHD: 'Bahrain', OMR: 'Oman',
    KWD: 'Kuwait', EUR: 'Europe', USD: 'Rest of world',
  };

  var rates = Object.assign({}, FALLBACK);

  function read(key) {
    try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; }
  }
  function write(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* private mode */ }
  }

  /* THE DISPLAY CURRENCY FOLLOWS THE DELIVERY COUNTRY.

     With Shopify Payments, picking a country in the localization selector
     would change the real currency by itself. Without it every market is AED,
     so the two controls would otherwise disagree — pick "Saudi Arabia" and
     still read dirhams, which is exactly the confusion this avoids.

     Shopify sets the country from the visitor's IP on a first visit, so this
     is also what delivers "show the right currency by IP" without any
     geolocation of our own.

     Countries not listed fall to USD, which is what the picker calls
     "Rest of world". */
  var COUNTRY_CURRENCY = {
    AE: 'AED', SA: 'SAR', BH: 'BHD', OM: 'OMR', KW: 'KWD', QA: 'USD',
    AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR',
    FI: 'EUR', FR: 'EUR', GR: 'EUR', HR: 'EUR', IE: 'EUR', IT: 'EUR',
    LT: 'EUR', LU: 'EUR', LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR',
    SI: 'EUR', SK: 'EUR',
  };

  function country() {
    var box = document.querySelector('[data-ef-currency]');
    var c = box && box.getAttribute('data-country');
    return c ? c.toUpperCase() : null;
  }

  function currencyForCountry() {
    var c = country();
    if (!c) return SHOP;
    var code = COUNTRY_CURRENCY[c] || 'USD';
    return OFFERED.indexOf(code) > -1 ? code : SHOP;
  }

  /* An explicit pick is remembered ALONGSIDE the country it was made under.
     Change the delivery country and the currency re-derives, because a
     stale pick from a previous country is not what the visitor meant. Keep
     the country and their pick stands. */
  function current() {
    var saved = null, savedFor = null;
    try {
      saved = localStorage.getItem(KEY);
      savedFor = localStorage.getItem(KEY + '-country');
    } catch (e) { /* ignore */ }
    if (OFFERED.indexOf(saved) > -1 && savedFor === country()) return saved;
    return currencyForCountry();
  }

  /* DISPLAY ROUNDING, so a converted price is as tidy as the AED one.
     The AED catalogue was rounded to .00/.50 on 25 Aug 2026; an exact
     conversion of that undoes the tidiness immediately — AED 1,750.00 becomes
     SAR 1,786.93, BHD 179.170, KWD 145.813.

     The step is per currency, not one number, because these units are worth
     very different amounts. AED 0.50 is about USD 0.14, so the step for each
     currency is whatever lands near that:

       SAR EUR USD   0.50   (2-decimal currencies)
       BHD OMR KWD   0.050  (3-decimal; one unit is ~2.65 USD, so a 0.50 step
                             would jump a cheap item by several dirhams)

     ALWAYS UP, never down — the customer is charged the AED figure, so a
     displayed price that rounds up means they pay slightly LESS than they
     were shown. Rounding down would do the opposite, which is the version
     that generates complaints. */
  var STEP = { SAR: 0.5, EUR: 0.5, USD: 0.5, BHD: 0.05, OMR: 0.05, KWD: 0.05 };

  function roundUpTo(value, step) {
    if (!step) return value;
    /* Work in integer units of the step to keep binary float out of it:
       179.17 / 0.05 is 3583.3999999999996, and ceil() on that is right by
       luck rather than by design. */
    var units = Math.round(value / step * 1e6) / 1e6;
    return Math.ceil(units) * step;
  }

  /* Intl gets the decimals right without a table: BHD, OMR and KWD are
     3-decimal currencies, everything else here is 2. Hand-rolling that is
     exactly the sort of detail that goes wrong quietly. */
  function format(amountMinor, code) {
    var major = (amountMinor / 100) * (rates[code] || 1);
    major = roundUpTo(major, STEP[code]);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: code, currencyDisplay: 'narrowSymbol',
      }).format(major);
    } catch (e) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(major);
      } catch (e2) {
        return code + ' ' + major.toFixed(2);
      }
    }
  }

  function paint() {
    var code = current();
    document.querySelectorAll('[data-ef-money]').forEach(function (el) {
      var minor = parseInt(el.getAttribute('data-ef-money'), 10);
      if (isNaN(minor)) return;
      /* Keep the server-rendered markup so switching back to AED restores the
         shop's own money format, app markup and all, rather than our guess. */
      if (!el.hasAttribute('data-ef-money-original')) {
        el.setAttribute('data-ef-money-original', el.innerHTML);
      }
      if (code === SHOP) {
        el.innerHTML = el.getAttribute('data-ef-money-original');
      } else {
        el.textContent = format(minor, code);
      }
    });
    document.querySelectorAll('[data-ef-currency-note]').forEach(function (el) {
      el.hidden = code === SHOP;
    });
    document.querySelectorAll('[data-ef-currency-value]').forEach(function (el) {
      el.textContent = code;
    });
    document.querySelectorAll('[data-ef-currency-option]').forEach(function (el) {
      var on = el.getAttribute('data-ef-currency-option') === code;
      el.classList.toggle('is-current', on);
      if (on) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
  }

  function choose(code) {
    if (OFFERED.indexOf(code) === -1) return;
    try {
      localStorage.setItem(KEY, code);
      localStorage.setItem(KEY + '-country', country() || '');
    } catch (e) { /* ignore */ }
    paint();
  }

  /* One refresh a day, and only if the visitor is actually using a non-shop
     currency — an AED visitor never needs a rate at all. */
  function refresh() {
    if (current() === SHOP) return;
    var cached = read(RATES_KEY);
    if (cached && cached.at && (Date.now() - cached.at) < DAY && cached.rates) {
      rates = Object.assign({}, FALLBACK, cached.rates);
      paint();
      return;
    }
    fetch('https://open.er-api.com/v6/latest/AED')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.rates) return;
        var fresh = {};
        OFFERED.forEach(function (c) {
          if (typeof data.rates[c] === 'number') fresh[c] = data.rates[c];
        });
        if (!Object.keys(fresh).length) return;
        rates = Object.assign({}, FALLBACK, fresh);
        write(RATES_KEY, { at: Date.now(), rates: fresh });
        paint();
      })
      .catch(function () { /* pegs stand */ });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[data-ef-currency-option]');
    if (!btn) return;
    e.preventDefault();
    choose(btn.getAttribute('data-ef-currency-option'));
    var box = btn.closest('details');
    if (box) box.open = false;
  });

  /* Cards and prices arrive after a filter or a pagination swap, so repaint
     when the DOM changes rather than only on load. */
  var pending = null;
  var mo = new MutationObserver(function () {
    if (pending) return;
    pending = setTimeout(function () { pending = null; paint(); }, 60);
  });

  function boot() {
    paint();
    refresh();
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.EFCurrency = { choose: choose, current: current, offered: OFFERED, labels: LABELS };
})();
